import { useEffect } from 'react';
import type { IAhuBoardProject, IAllPermissions, IBoardUser } from 'models';

const AHU_CLASSES = [
  'ahu-access-permission',
  'ahu-view-permission',
  'ahu-no_access-permission',
] as const;

const MODEL_VIEW_CLASSES = [
  'model_view-access-permission',
  'model_view-view-permission',
  'model_view-no_access-permission',
] as const;

export type AhuPermissionLevel = 'access' | 'view' | 'no_access';

/**
 * Pure derivation of the MainView permission pipeline (§2.4 render +
 * _applyAhuPermissions): `ahu_board_items` rows 'ahu'/'model_view' map
 * access→'access' else 'view'; the AHU permission downgrades to 'view' when
 * `project.is_critical_task_started && user.access_type === 'project'` or when
 * `!project.editable && user.access_type === 'network'` (network read-only).
 */
export function deriveAhuPermissions(
  permissions: IAllPermissions | undefined,
  project: IAhuBoardProject | undefined,
  user: IBoardUser | undefined
): { ahu: AhuPermissionLevel; modelView: AhuPermissionLevel } | null {
  const items = permissions?.permissions?.ahu_board_items;
  if (!items) return null;

  let ahu: AhuPermissionLevel = 'no_access';
  let modelView: AhuPermissionLevel = 'no_access';
  for (const row of items) {
    if (row.item === 'ahu') ahu = row.access ? 'access' : 'view';
    else if (row.item === 'model_view') modelView = row.access ? 'access' : 'view';
  }

  const accessType = user?.access_type;
  const readonly = project?.editable === false && accessType === 'network';
  if (project?.is_critical_task_started && accessType === 'project' && ahu === 'access') {
    ahu = 'view';
  }
  if (readonly) ahu = 'view';

  return { ahu, modelView };
}

/**
 * Applies the body permission classes (parity 10/11/14):
 * `ahu-{access|view|no_access}-permission` + `model_view-<perm>-permission`.
 * CSS gates the whole UI off these; the canvas right-click gate (parity 11)
 * reads `ahu-view-permission` in 6d. Recomputes when the project refetches
 * (workflow:done parity 14 — RTKQ invalidation re-runs this hook). Classes are
 * applied only once permissions have loaded, like the legacy render promise.
 */
export function useAhuPermissions(
  permissions: IAllPermissions | undefined,
  project: IAhuBoardProject | undefined,
  user: IBoardUser | undefined
): void {
  const derived = deriveAhuPermissions(permissions, project, user);
  const ahuPerm = derived?.ahu;
  const modelViewPerm = derived?.modelView;

  useEffect(() => {
    if (!ahuPerm || !modelViewPerm) return undefined;
    const body = document.body;
    body.classList.remove(...AHU_CLASSES);
    body.classList.add(`ahu-${ahuPerm}-permission`);
    body.classList.remove(...MODEL_VIEW_CLASSES);
    body.classList.add(`model_view-${modelViewPerm}-permission`);
    return () => {
      body.classList.remove(...AHU_CLASSES, ...MODEL_VIEW_CLASSES);
    };
  }, [ahuPerm, modelViewPerm]);
}
